import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { rolledOverDate, todayLocal } from "./date";
import { onResume } from "./appResume";

interface Ctx {
  selectedDate: string;
  setSelectedDate: (d: string) => void;
  isToday: boolean;
}

const SelectedDateCtx = createContext<Ctx | null>(null);

export function SelectedDateProvider({ children }: { children: ReactNode }) {
  const [selectedDate, setSelectedDate] = useState(todayLocal());
  // The calendar date this provider last believed in. An app left open across
  // midnight — or backgrounded overnight, which is the common case — would
  // otherwise keep logging into yesterday.
  const knownToday = useRef(selectedDate);

  useEffect(() => onResume(() => {
    const now = todayLocal();
    if (now === knownToday.current) return;
    setSelectedDate((cur) => rolledOverDate(cur, knownToday.current, now));
    knownToday.current = now;
  }), []);

  const isToday = selectedDate === todayLocal();
  return (
    <SelectedDateCtx.Provider value={{ selectedDate, setSelectedDate, isToday }}>
      {children}
    </SelectedDateCtx.Provider>
  );
}

export function useSelectedDate(): Ctx {
  const ctx = useContext(SelectedDateCtx);
  if (!ctx) throw new Error("useSelectedDate must be used inside SelectedDateProvider");
  return ctx;
}
